import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Dict, Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { CeaModelResult } from '/server/integrations/ceaPricingModel';
import { Project } from '/imports/api/projects';

export const projectInputFields = ['address', 'systemSize', 'annualProduction', 'annualConsumption', 'epcPrice', 'avoidedCost',
  'installType', 'essCapacity', 'essSize', 'essEpcPrice', 'essAvoidedCost', 'image'] as const;

type ProjectInputFields = typeof projectInputFields[number];

export interface ProjectQuote extends Doc {
  uid: string;
  projectId: string;
  name: string;
  proposalType: 'ess' | 'essSolar';
  quoteType: 'ceaDetailed' | 'ceaIndicative';
  status: 'generated' | 'active' | 'requested';
  projectInputs?: Pick<Project, ProjectInputFields>;
  modelResult?: CeaModelResult;
  modelFile?: FileMeta;
  pdf?: FileMeta;
  presenter?: { name: string, email: string, phone: string, image: string, logo: string, website: string };
}
export type Model = ProjectQuote;

export const ProjectQuotes = new Mongo.Collection<ProjectQuote>('projectQuotes');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: ProjectQuotes,
  editableFields: ['active', 'projectId', 'name', 'proposalType', 'quoteType', 'status', 'presenter'
  ],
  fieldLimits: {
    'name': 512, 'projectId': 64, 'proposalType': 64, 'quoteType': 64, 'status': 64,
  },
  listMethod: DeclareMethod<ListMethodArg<ProjectQuote>, ListMethodResponse<ProjectQuote, Refs>>('listProjectQuotes'),
  getMethod: DeclareMethod<string, GetMethodResponse<ProjectQuote, Refs>>('getProjectQuote'),
  createMethod: DeclareMethod<Partial<ProjectQuote>, string>('createProjectQuote'),
  updateMethod: DeclareMethod<Partial<ProjectQuote>, string>('updateProjectQuote'),
  deleteMethod: DeclareMethod<string>('deleteProjectQuote'),
  pathPrefix: '/projects',
  asString: (document) => document.name || document._id,
  modelName: strings.Quote(),
  pluralName: strings.Quotes(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (project: Partial<ProjectQuote>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'projectId', { required: true });
    validateString(errors, project, 'name', { required: true });
    validateString(errors, project, 'proposalType', { required: true });
    validateString(errors, project, 'quoteType', { required: true });
    validateString(errors, project, 'status', { required: true });
    return errors;
  },
};

export const ProposalTypeOptions = [
  { value: 'essSolar', label: strings.SolarPlusEnergyStorage() },
  { value: 'ess', label: strings.EnergyStorageOnly() },
  // { value: 'solarOnly', label: strings.SolarOnly() },
  // { value: 'solarUpgrades', label: strings.SolarPlusUpgrades() },
];

const proposalTypeStringMap = ProposalTypeOptions.reduce((map: Dict<string>, opt) => {
  map[opt.value] = opt.label;
  return map;
}, {});

export const getProjectQuoteForJwt = DeclareMethod<{ quoteId: string | null, jwt: string | null }, ProjectQuote & { _project: Project }>('getProjectQuoteForJwt');
export const getNumProjectQuotes = DeclareMethod<{ projectId: string }, number>('getNumProjectQuotes');
export const generateQuoteProposalPdf = DeclareMethod<{ quoteId: string }, FileMeta>('generateQuoteProposalPdf');
export const requestCustomQuote = DeclareMethod<{ projectId: string, proposalType: ProjectQuote['proposalType'] }, void>('requestCustomQuote');
export const requestPreliminaryProposal = DeclareMethod<{ projectId: string }, void>('requestPreliminaryProposal');

/** Localized label for a quote's proposal type. */
export const projectQuoteProposalType = <T extends { proposalType?: string }>(quote: T) => proposalTypeStringMap[quote.proposalType || ''];
/**
 * Localized label for a quote's type — indicative or detailed.
 * Takes a plain string, not ProjectQuote['quoteType']: quotes generated before the indicative/detailed
 * split (Dec 2025 – Jan 2026) carry a bare 'cea', and they are still listed. Those stay unlabelled
 * rather than being guessed into one bucket or the other.
 */
export const projectQuoteType = <T extends { quoteType?: string }>(quote: T) => {
  if (quote.quoteType === 'ceaIndicative') return strings.Indicative();
  if (quote.quoteType === 'ceaDetailed') return strings.Detailed();
  return '—';
};
/** Localized label for a quote's status. */
export const projectQuoteStatus = <T extends { status?: ProjectQuote['status'] }>(quote: T) => {
  const status = quote.status;
  if (status === 'generated') return strings.Generated();
  if (status === 'active') return strings.Active();
  if (status === 'requested') return strings.Requested();
  return strings.Generated();
};
/**
 * Finds the active quote for a project.
 * @param projectId - project to look up
 * @returns the active ProjectQuote, or undefined
 */
export const projectActiveQuote = async (projectId: string) => await ProjectQuotes.findOneAsync({ projectId, status: 'active' });

Meteor.isServer && Meteor.startup(async () => {
  await ProjectQuotes.createIndexAsync({ projectId: 1 });
});

// interface CreditPpaSolarOnlyQuote {
//   buyout_yr1_amt_solar: string;
//   buyout_yr1_amt_solar_ess: string;
//   buyout_yr2_amt_solar: string;
//   buyout_yr2_amt_solar_ess: string;
//   first_payment_date: string;
//   model_version_number: number;
//   ppa_rate_solar: string;
//   ppa_rate_solar_ess: string;
//   pricing_model_response: number;
//   prop_value_increase_solar: string;
//   prop_value_increase_solar_ess: string;
//   total_project_cost: string;
//   solar_ac: {
//     data_by_years: string[];
//     term_total: string;
//     year1: string;
//     year5cumulative: string | null;
//   },
//   solar_assessment: {
//     data_by_years: string[];
//     term_total: string;
//     year1: string;
//     year5cumulative: string | null;
//   },
//   solar_ess_ppa_payment: {
//     data_by_years: string[];
//     term_total: string;
//     year1: string;
//     year5cumulative: string | null;
//   },
//   solar_prod: {
//     data_by_years: number[];
//     term_total: string;
//     year1: number;
//     year5cumulative: string | null;
//   },
//   solar_savings: {
//     data_by_years: string;
//     term_total: string;
//     year1: string;
//     year5cumulative: string | null;
//   },
//   storage_ac: {
//     data_by_years: string[];
//     term_total: string[];
//     year1: string[];
//     year5cumulative: string | null;
//   },
//   total_savings: {
//     data_by_years: string[];
//     term_total: string;
//     year1: string;
//     year5cumulative: string | null;
//   },
//   qb_data: {
//     proposal_id: number;
//     model_version: number;
//     utility_name: string;
//     estimated_annual_energy_consumption: string;
//     consumption_offset: number;
//     avoided_utility_cost_escalator: number;
//     pv_system_type: string;
//     pv_useful_lifetime: number;
//     module_degradation_rate: number;
//     solar_system_size: number;
//     yr_1_pv_production: number;
//     total_pv_epc_price: number;
//     pv_cost_itc_eligibility: number;
//     term: number;
//     comb_1st_pmt_yr_ppa_rate: number;
//     escalator: number;
//     total_yr_1_savings: number;
//     total_yr_5_cum_savings: number;
//     total_term_cum_savings: number;
//     storage_system_size_kw: number;
//     storage_system_size_kwh: number;
//     storage_epc_build_cost: number;
//     storage_dev_fee: number;
//     storage_avoided_cost: number;
//     solar_ppa_rate: number;
//     solar_ppa_escalator: number;
//     expected_financing_close_date: string;
//     pis_date: string;
//     luminia_platform_access_fee: number;
//     luminia_dev_fee: number;
//     partner_dev_fee: number;
//     total_sales_fee: number;
//     solar_sales_fee_affiliate: number;
//     solar_sales_fee_referral: number;
//     storage_sales_fee_affiliate: number;
//     storage_sales_fee_referral: number;
//     total_closing_costs: number;
//     total_project_cost: number;
//     estimated_m1f_funding_date: string;
//     estimated_m1f_funding_amount: number;
//     estimated_m2f_funding_date: string;
//     estimated_m2f_funding_amount: number;
//     estimated_m3f_funding_date: string;
//     estimated_m3f_funding_amount: number;
//     estimated_m4f_funding_date: string;
//     estimated_m4f_funding_amount: number;
//     estimated_m5f_funding_date: string;
//     estimated_m5f_funding_amount: number;
//     estimated_m6f_funding_date: string;
//     estimated_m6f_funding_amount: number;
//     estimated_m7f_funding_date: string;
//     estimated_m7f_funding_amount: number;
//     model_type: string;
//     origination_channel_sales_fee: number;
//   }
// }